/**
 * posSoundPrefs.ts — Preferencias de sonido y avisos del POS por negocio.
 *
 * QUÉ HACE: Lee y guarda (ACID vía `transactionalStore`) qué avisos sonoros
 *   del POS están activos, su volumen, si el pedido nuevo se anuncia por voz,
 *   si se muestra la notificación del sistema y el mensaje personalizado por
 *   tipo de pedido (mesa / domicilio / local). Es una preferencia del negocio
 *   en el dispositivo: no viaja a la nube. Emite `altenot:pos-sound-prefs` al
 *   guardar para que los paneles abiertos se sincronicen sin recargar.
 * QUÉ EXPORTA: PosSoundPrefs, DEFAULT_POS_SOUND_PREFS, POS_SOUND_PREFS_EVENT,
 *   MAX_POS_ALERT_MESSAGE_LENGTH, loadPosSoundPrefs, persistPosSoundPrefs.
 * QUÉ NO DEBE HACER: No reproduce audio ni conoce React.
 */
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

export interface PosSoundPrefs {
  /** Beep al agregar un ítem al carrito. */
  add: boolean;
  /** Beep al quitar/bajar cantidad/poner la venta en espera. */
  remove: boolean;
  /** Aviso de dos tonos al llegar un pedido nuevo (mesa/domicilio/local). */
  pending: boolean;
  /** Volumen (0.1–1) aplicado a todos los avisos del POS. */
  volume: number;
  /** Lee en voz alta el mensaje personalizado del pedido nuevo. */
  voice: boolean;
  /** Muestra la notificación del sistema al llegar un pedido nuevo. */
  notify: boolean;
  /** Mensaje personalizado para pedidos nuevos de mesa. */
  mensajeMesa: string;
  /** Mensaje personalizado para pedidos nuevos a domicilio. */
  mensajeDomicilio: string;
  /** Mensaje personalizado para pedidos nuevos de tienda «local». */
  mensajeLocal: string;
}

export const DEFAULT_POS_SOUND_PREFS: PosSoundPrefs = {
  add: true,
  remove: true,
  pending: true,
  volume: 1,
  voice: true,
  notify: true,
  mensajeMesa: "Pedido nuevo en mesa",
  mensajeDomicilio: "Pedido nuevo a domicilio",
  mensajeLocal: "Pedido nuevo Tienda virtual",
};

export const POS_SOUND_PREFS_EVENT = "altenot:pos-sound-prefs";

/** Largo máximo de un mensaje personalizado (control de storage y de voz). */
export const MAX_POS_ALERT_MESSAGE_LENGTH = 120;

const keyFor = (businessId?: string | null): string =>
  `caja_pos_sound_${(businessId || "default").trim() || "default"}`;

const normalizeMessage = (value: unknown, fallback: string): string => {
  if (typeof value !== "string") return fallback;
  const clean = value.trim().slice(0, MAX_POS_ALERT_MESSAGE_LENGTH);
  return clean || fallback;
};

/** Mensaje anterior del local: se migra al nuevo texto por defecto. */
const LEGACY_MENSAJE_LOCAL = "Pedido nuevo para el local";

const normalizeLocalMessage = (value: unknown): string => {
  const msg = normalizeMessage(value, DEFAULT_POS_SOUND_PREFS.mensajeLocal);
  return msg === LEGACY_MENSAJE_LOCAL
    ? DEFAULT_POS_SOUND_PREFS.mensajeLocal
    : msg;
};

/** Normaliza cualquier valor guardado a un contrato válido (defaults ON). */
function normalizePosSoundPrefs(
  raw: Partial<PosSoundPrefs> | null | undefined,
): PosSoundPrefs {
  const volume =
    typeof raw?.volume === "number" && Number.isFinite(raw.volume)
      ? raw.volume
      : DEFAULT_POS_SOUND_PREFS.volume;
  return {
    add: raw?.add !== false,
    remove: raw?.remove !== false,
    pending: raw?.pending !== false,
    volume: Math.min(1, Math.max(0.1, volume)),
    voice: raw?.voice !== false,
    notify: raw?.notify !== false,
    mensajeMesa: normalizeMessage(
      raw?.mensajeMesa,
      DEFAULT_POS_SOUND_PREFS.mensajeMesa,
    ),
    mensajeDomicilio: normalizeMessage(
      raw?.mensajeDomicilio,
      DEFAULT_POS_SOUND_PREFS.mensajeDomicilio,
    ),
    mensajeLocal: normalizeLocalMessage(raw?.mensajeLocal),
  };
}

/** Preferencias guardadas del negocio (defaults si no hay nada). */
export function loadPosSoundPrefs(businessId?: string | null): PosSoundPrefs {
  try {
    const raw = txRead<Partial<PosSoundPrefs> | null>(keyFor(businessId), null);
    if (!raw || typeof raw !== "object") {
      return { ...DEFAULT_POS_SOUND_PREFS };
    }
    return normalizePosSoundPrefs(raw);
  } catch {
    return { ...DEFAULT_POS_SOUND_PREFS };
  }
}

/** Guarda las preferencias del negocio y avisa a los paneles abiertos. */
export function persistPosSoundPrefs(
  businessId: string | undefined | null,
  prefs: PosSoundPrefs,
): PosSoundPrefs {
  const clean = normalizePosSoundPrefs(prefs);
  txWrite(keyFor(businessId), clean);
  try {
    window.dispatchEvent(new CustomEvent(POS_SOUND_PREFS_EVENT));
  } catch {
    /* SSR / entornos sin window */
  }
  return clean;
}
